import { useMemo, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { LoadingState, Notice, ProductList, traceButtonClass, describeWorkspace } from '@traceio-org/auth-ui';
import type { ProductKey } from '@traceio-org/platform-contracts';
import { useMeAccess } from '../app/access';
import { isSessionEnded, readHubState, useDecideAndFollow } from '../app/hooks';
import { AccountActions, DecideFailedNotice, HubFrame, PageHeading } from '../app/ui';
import { WorkspaceSettingsLink, settingsHeaderActions } from '../settings/entry';

/**
 * /select-product: the products of one workspace (the context in the hub state, or the only one the
 * person has), with their official lockups. Choosing one asks platform-api to decide.
 */
export function SelectProductPage() {
  const location = useLocation();
  const { contextId, returnPath } = useMemo(() => readHubState(location.state), [location.state]);
  const [access, retry] = useMeAccess(null);
  const { run: runDecide, failed: decideFailed } = useDecideAndFollow();
  const [busyKey, setBusyKey] = useState<ProductKey | null>(null);
  const [lastChoice, setLastChoice] = useState<ProductKey | null>(null);

  const contexts = access.status === 'ready' ? access.data.contexts : [];
  const context = contexts.find((c) => c.id === contextId) ?? (contexts.length === 1 ? contexts[0] : undefined);

  if (access.status === 'ready' && contexts.length === 0) return <Navigate to="/no-workspace" replace />;
  if (access.status === 'ready' && !context) {
    return <Navigate to="/select-organization" replace state={returnPath ? { returnPath } : undefined} />;
  }

  async function choose(productKey: ProductKey) {
    if (!context) return;
    setBusyKey(productKey);
    setLastChoice(productKey);
    const plan = await runDecide({ contextId: context.id, productKey, ...(returnPath ? { returnPath } : {}) });
    if (!plan || plan.type === 'navigate') setBusyKey(null);
  }

  return (
    <HubFrame productKey={null} headerActions={settingsHeaderActions(contexts)}>
      <PageHeading title="Choose a product" lead={context ? describeWorkspace(context).name : undefined} />
      {decideFailed && lastChoice && busyKey === null ? (
        <DecideFailedNotice onRetry={() => void choose(lastChoice)} />
      ) : null}
      {access.status === 'loading' ? <LoadingState label="Loading your products" /> : null}
      {access.status === 'error' && !isSessionEnded(access.error) ? (
        <Notice tone="error" live="assertive" title="We could not load your products.">
          <button type="button" className={traceButtonClass('primary', { block: true })} onClick={retry}>
            Try again
          </button>
        </Notice>
      ) : null}
      {context ? <ProductList products={context.products} onSelect={choose} busyKey={busyKey} /> : null}
      {context ? <WorkspaceSettingsLink context={context} /> : null}
      {contexts.length > 1 ? (
        <Link className="trace-auth-link" to="/select-organization" state={returnPath ? { returnPath } : undefined}>
          Choose another workspace
        </Link>
      ) : null}
      <AccountActions productKey={null} />
    </HubFrame>
  );
}
